import { DateTime } from 'luxon'

/**
 * Combines a date and time string into an ISO_LOCAL_DATE_TIME string (YYYY-MM-DDTHH:mm:ss)
 * Combines date and time as-is without timezone conversion
 * @param date - Date string (ISO format or Date object) or datetime string (will extract date part)
 * @param time - Time string in format "HH:MM AM/PM" or "HH:MM"
 * @returns ISO_LOCAL_DATE_TIME string (YYYY-MM-DDTHH:mm:ss) or empty string if inputs are invalid
 */
export const combineDateAndTime = (
  date: string | Date | null | undefined,
  time: string | null | undefined
): string => {
  if (!date || !time) return ''

  try {
    // Parse the date - if it's a datetime, extract just the date part
    let year: number, month: number, day: number
    if (typeof date === 'string') {
      // If it's an ISO string, extract just the date part
      const datePart = date.split('T')[0]
      const [y, m, d] = datePart.split('-').map(Number)
      year = y
      month = m - 1 // JavaScript months are 0-indexed
      day = d
    } else {
      year = date.getFullYear()
      month = date.getMonth()
      day = date.getDate()
    }
    
    // Parse the time string (format: "HH:MM AM/PM" or "HH:MM")
    const timeParts = time.trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
    if (!timeParts) return ''

    let hours = parseInt(timeParts[1], 10)
    const minutes = parseInt(timeParts[2], 10)
    const ampm = timeParts[3]?.toUpperCase()

    // Convert to 24-hour format
    if (ampm === 'PM' && hours !== 12) {
      hours += 12
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0
    }

    // Validate hours and minutes
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return ''

    // Create DateTime without timezone (system default), format as-is
    // Backend sends/receives as LocalDateTime format (no timezone info)
    const dt = DateTime.fromObject(
      { year, month: month + 1, day, hour: hours, minute: minutes }
    )
    
    if (!dt.isValid) return ''
    
    // Format as ISO_LOCAL_DATE_TIME (YYYY-MM-DDTHH:mm:ss) without timezone conversion
    return dt.toFormat('yyyy-MM-dd\'T\'HH:mm:ss')
  } catch (error) {
    console.error('Error combining date and time:', error)
    return ''
  }
}

/**
 * Splits an ISO datetime string into date and time components
 * Displays timestamp as-is without timezone conversion
 * @param datetime - ISO datetime string (from backend)
 * @returns Object with date (ISO date string) and time (HH:MM AM/PM format) or null
 */
export const splitDateTime = (
  datetime: string | null | undefined
): { date: string; time: string } | null => {
  if (!datetime) return null

  try {
    // Parse as-is without timezone conversion (backend sends timestamps without timezone info)
    const dt = DateTime.fromISO(datetime)
    if (!dt.isValid) return null

    return {
      date: dt.toFormat('yyyy-MM-dd'),
      time: dt.toFormat('h:mm a'),
    }
  } catch (error) {
    console.error('Error splitting datetime:', error)
    return null
  }
}

/**
 * Extracts only the date part from a datetime string
 * Displays timestamp as-is without timezone conversion
 * @param datetime - ISO datetime string (from backend)
 * @returns ISO date string (YYYY-MM-DD) or empty string
 */
export const extractDate = (datetime: string | null | undefined): string => {
  if (!datetime) return ''
  try {
    // Parse as-is without timezone conversion (backend sends timestamps without timezone info)
    const dt = DateTime.fromISO(datetime)
    if (!dt.isValid) return ''
    return dt.toFormat('yyyy-MM-dd')
  } catch (error) {
    return ''
  }
}

/**
 * Extracts only the time part from a datetime string.
 * Displays timestamp as-is without timezone conversion.
 * When the time is 00:00:00 (midnight), returns empty string so the UI can show "no time" instead of "12:00 AM".
 * @param datetime - ISO datetime string (from backend)
 * @returns Time string in HH:MM AM/PM format or empty string if midnight / no time
 */
export const extractTime = (datetime: string | null | undefined): string => {
  if (!datetime) return ''
  try {
    // Parse as-is without timezone conversion (backend sends timestamps without timezone info)
    const dt = DateTime.fromISO(datetime)
    if (!dt.isValid) return ''
    // If time is midnight (00:00:00), treat as "no time" and keep time part blank
    if (dt.hour === 0 && dt.minute === 0 && dt.second === 0) return ''
    return dt.toFormat('h:mm a')
  } catch (error) {
    return ''
  }
}

